// ============================================================================
// Google Analytics 4 — Lightweight wrapper
// ============================================================================
// Usage:
//   import { trackEvent, trackPageView } from '../utils/analytics';
//   trackEvent('login', { method: 'email' });
//   trackPageView('/dashboard');
// ============================================================================

const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;
const ANALYTICS_DEBUG = process.env.REACT_APP_ANALYTICS_DEBUG === 'true';

function logAnalyticsWarning(message, details = {}) {
  if (!ANALYTICS_DEBUG) return;
  console.warn('[Analytics]', message, details);
}

function hasMeasurementId() {
  return !!GA_MEASUREMENT_ID;
}

function isAnalyticsEnabled() {
  if (!hasMeasurementId()) return false;
  return window[`ga-disable-${GA_MEASUREMENT_ID}`] !== true;
}

export function getAnalyticsStatus() {
  return {
    measurementIdConfigured: hasMeasurementId(),
    measurementId: GA_MEASUREMENT_ID || null,
    initialized: !!window.__ga_initialized,
    gtagAvailable: typeof window.gtag === 'function',
    scriptLoaded: !!window.__ga_script_loaded,
    scriptLoadError: !!window.__ga_script_error,
    consentGranted: hasMeasurementId() ? window[`ga-disable-${GA_MEASUREMENT_ID}`] !== true : false,
  };
}

function ensureGtagStub() {
  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    window.gtag = function () {
      window.dataLayer.push(arguments);
    };
  }
}

/**
 * Initialize GA4 by injecting the gtag.js script (called once on app load).
 * Does nothing if the measurement ID is missing (e.g. local dev without env var).
 */
export function initGA() {
  if (!hasMeasurementId()) {
    logAnalyticsWarning('No GA_MEASUREMENT_ID configured; GA initialization skipped.');
    return;
  }

  if (typeof window !== 'undefined') {
    window.__getAnalyticsStatus = getAnalyticsStatus;
  }

  ensureGtagStub();

  // Avoid double-initialization
  if (window.__ga_initialized) return;
  window.__ga_initialized = true;

  // Respect privacy by default. Consent updates can switch to granted later.
  window[`ga-disable-${GA_MEASUREMENT_ID}`] = true;

  window.gtag('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    wait_for_update: 500,
  });

  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID, {
    send_page_view: false, // We send page views manually via RouteTracker
    anonymize_ip: true,
  });

  // 2. Load the gtag.js script asynchronously (non-blocking)
  const script = document.createElement('script');
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  script.async = true;
  script.onload = () => {
    window.__ga_script_loaded = true;
    window.__ga_script_error = false;
  };
  script.onerror = () => {
    window.__ga_script_loaded = false;
    window.__ga_script_error = true;
    logAnalyticsWarning('Failed to load gtag.js script.', getAnalyticsStatus());
  };
  document.head.appendChild(script);
}

/**
 * Update GA consent mode and runtime tracking state.
 * @param {boolean} granted - whether analytics consent is granted.
 */
export function setAnalyticsConsent(granted) {
  if (!hasMeasurementId()) return;

  initGA();
  ensureGtagStub();

  window[`ga-disable-${GA_MEASUREMENT_ID}`] = !granted;

  window.gtag('consent', 'update', {
    analytics_storage: granted ? 'granted' : 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });

  if (granted) {
    window.gtag('event', 'analytics_health_ping', {
      source: 'consent_update',
      page_path: window.location.pathname,
      non_interaction: true,
    });
  }
}

/**
 * Track a page view. Call this on every route change.
 * @param {string} path — e.g. '/dashboard'
 * @param {string} [title] — optional page title
 */
export function trackPageView(path, title) {
  if (!window.gtag) {
    logAnalyticsWarning('trackPageView skipped because gtag is unavailable.', { path, ...getAnalyticsStatus() });
    return;
  }

  if (!isAnalyticsEnabled()) {
    logAnalyticsWarning('trackPageView skipped because analytics consent is denied.', { path, ...getAnalyticsStatus() });
    return;
  }

  const pageLocation = `${window.location.origin}${path}`;

  window.gtag('event', 'page_view', {
    page_path: path,
    page_title: title || document.title,
    page_location: pageLocation,
    page_referrer: document.referrer || undefined,
  });
}

/**
 * Track a custom event.
 * @param {string} eventName — GA4 event name (e.g. 'login', 'sign_up', 'search')
 * @param {Object} [params] — event parameters
 */
export function trackEvent(eventName, params = {}) {
  if (!window.gtag) {
    logAnalyticsWarning('trackEvent skipped because gtag is unavailable.', {
      eventName,
      ...getAnalyticsStatus(),
    });
    return;
  }

  if (!isAnalyticsEnabled()) {
    logAnalyticsWarning('trackEvent skipped because analytics consent is denied.', {
      eventName,
      ...getAnalyticsStatus(),
    });
    return;
  }

  window.gtag('event', eventName, params);
}

// ============================================================================
// Pre-built event helpers for key user actions
// ============================================================================

export const analytics = {
  /** User logged in successfully */
  login: (method = 'email') =>
    trackEvent('login', { method }),

  /** User signed up successfully */
  signUp: (method = 'email') =>
    trackEvent('sign_up', { method }),

  /** User searched for properties */
  propertySearch: (filters = {}) =>
    trackEvent('search', {
      search_term: filters.city || filters.keyword || '',
      content_type: 'property',
      property_type: filters.propertyType || '',
      city: filters.city || '',
    }),

  /** User viewed a property detail page */
  propertyView: (listingKey, propertyInfo = {}) =>
    trackEvent('view_item', {
      content_type: 'property',
      item_id: listingKey,
      city: propertyInfo.city || '',
      price: propertyInfo.price || '',
    }),

  /** User sent a chat message to the AI agent */
  chatMessage: () =>
    trackEvent('chat_message', {
      content_type: 'ai_chat',
    }),

  /** Search response quality for behavior/funnel analysis */
  propertySearchResults: ({ resultCount = 0, page = 1, city = '', propertyType = '' } = {}) =>
    trackEvent('property_search_results', {
      result_count: Number(resultCount) || 0,
      page: Number(page) || 1,
      city,
      property_type: propertyType,
    }),

  /** User opens property detail from results */
  propertyOpenDetail: (listingKey, source = 'unknown') =>
    trackEvent('select_item', {
      item_id: listingKey,
      content_type: 'property',
      source,
    }),

  /** User toggles watchlist state for a property */
  propertyWatchlistToggle: (listingKey, inWatchlist) =>
    trackEvent('property_watchlist_toggle', {
      item_id: listingKey,
      in_watchlist: !!inWatchlist,
      content_type: 'property',
    }),

  /** Backward-compatible alias for older favorites calls */
  propertyFavoriteToggle: (listingKey, favorited) =>
    trackEvent('property_watchlist_toggle', {
      item_id: listingKey,
      in_watchlist: !!favorited,
      content_type: 'property',
    }),

  /** Dashboard quick navigation interactions */
  dashboardNavigate: (destination, source = 'dashboard') =>
    trackEvent('dashboard_navigation_click', {
      destination,
      source,
    }),

  /** User opens a property from dashboard */
  dashboardOpenProperty: (propertyId, status = '', transactionType = '') =>
    trackEvent('dashboard_open_property', {
      property_id: String(propertyId),
      status,
      transaction_type: transactionType,
    }),

  /** Profile interactions */
  profileEditToggle: (isEditing) =>
    trackEvent('profile_edit_toggle', {
      is_editing: !!isEditing,
    }),

  profileSave: (role = '') =>
    trackEvent('profile_save', {
      role,
    }),

  profileLogout: (role = '') =>
    trackEvent('profile_logout', {
      role,
    }),

  /** My-property page interactions */
  myPropertySave: ({ mode = 'update', propertyId = '', status = '', transactionType = '', statusChanged = false } = {}) =>
    trackEvent('my_property_save', {
      mode,
      property_id: String(propertyId || ''),
      status,
      transaction_type: transactionType,
      status_changed: !!statusChanged,
    }),

  myPropertyPhotoUpload: (fileCount = 0) =>
    trackEvent('my_property_photo_upload', {
      file_count: Number(fileCount) || 0,
    }),

  myPropertyPhotoDelete: (propertyId = '') =>
    trackEvent('my_property_photo_delete', {
      property_id: String(propertyId || ''),
    }),

  myPropertyDocumentUpload: ({ fileCount = 0, documentType = '', autoFilledFields = 0 } = {}) =>
    trackEvent('my_property_document_upload', {
      file_count: Number(fileCount) || 0,
      document_type: documentType,
      auto_filled_fields: Number(autoFilledFields) || 0,
    }),

  myPropertyDocumentDelete: ({ propertyId = '', documentType = '' } = {}) =>
    trackEvent('my_property_document_delete', {
      property_id: String(propertyId || ''),
      document_type: documentType,
    }),

  myPropertyMLSHistoryCheck: ({ hasMatches = false, matchCount = 0 } = {}) =>
    trackEvent('my_property_mls_history_check', {
      has_matches: !!hasMatches,
      match_count: Number(matchCount) || 0,
    }),

  myPropertyMLSImport: (conflictCount = 0) =>
    trackEvent('my_property_mls_import', {
      conflict_count: Number(conflictCount) || 0,
    }),

  myPropertyMLSImportConflictsResolved: (selectedCount = 0) =>
    trackEvent('my_property_mls_import_conflicts_resolved', {
      selected_count: Number(selectedCount) || 0,
    }),

  /** User clicked Generate CMA Report */
  cmaReportGenerate: (propertyId = '') =>
    trackEvent('cma_report_generate', {
      property_id: String(propertyId || ''),
      content_type: 'property',
    }),

  /** User downloaded an existing CMA report */
  cmaReportDownload: (propertyId = '') =>
    trackEvent('cma_report_download', {
      property_id: String(propertyId || ''),
      content_type: 'property',
    }),

  /** User opened/viewed a CMA report in the viewer */
  cmaReportView: (propertyId = '') =>
    trackEvent('cma_report_view', {
      property_id: String(propertyId || ''),
      content_type: 'property',
    }),

  /** Marketing Strategy report generated */
  marketingStrategyGenerate: (propertyId = '') =>
    trackEvent('marketing_strategy_generate', {
      property_id: String(propertyId || ''),
      content_type: 'property',
    }),

  /** Marketing Strategy report downloaded */
  marketingStrategyDownload: (propertyId = '') =>
    trackEvent('marketing_strategy_download', {
      property_id: String(propertyId || ''),
      content_type: 'property',
    }),

  /** Marketing Strategy report viewed */
  marketingStrategyView: (propertyId = '') =>
    trackEvent('marketing_strategy_view', {
      property_id: String(propertyId || ''),
      content_type: 'property',
    }),

  /** Marketing material PDF generated (brochure/postcard/flyer) */
  marketingMaterialGenerate: (propertyId = '', materialType = '') =>
    trackEvent('marketing_material_generate', {
      property_id: String(propertyId || ''),
      material_type: String(materialType || ''),
    }),

  /** Marketing social drafts generated */
  marketingSocialDraft: (propertyId = '', channels = '') =>
    trackEvent('marketing_social_draft', {
      property_id: String(propertyId || ''),
      channels: String(channels || ''),
    }),

  /** Marketing email draft generated */
  marketingEmailDraft: (propertyId = '', audience = '') =>
    trackEvent('marketing_email_draft', {
      property_id: String(propertyId || ''),
      audience: String(audience || ''),
    }),

  /** Marketing Studio page opened */
  marketingStudioOpen: (propertyId = '', tab = '') =>
    trackEvent('marketing_studio_open', {
      property_id: String(propertyId || ''),
      tab: String(tab || ''),
    }),

  /** Contact page CTA button clicked (chat / schedule / email) */
  contactChannelClick: (channel = '') =>
    trackEvent('contact_channel_click', {
      channel,
    }),

  /** User submitted the Contact support form */
  contactSupportSubmit: ({ authenticated = false } = {}) =>
    trackEvent('contact_support_submit', {
      authenticated: authenticated ? 'yes' : 'no',
    }),

  /** Market page CTA button clicked */
  marketPageCTAClick: (action = '') =>
    trackEvent('market_page_cta_click', {
      action,
    }),

  /** FAQ page CTA button clicked */
  faqCTAClick: (action = '') =>
    trackEvent('faq_cta_click', {
      action,
    }),

  /** User opened a property card from My Properties list */
  myPropertiesPropertyOpen: (propertyId = '') =>
    trackEvent('my_properties_property_open', {
      property_id: String(propertyId || ''),
    }),

  /** User clicked Add New Property on My Properties page */
  myPropertiesAddProperty: () =>
    trackEvent('my_properties_add_property', {}),

  /** User initiated delete from My Properties page */
  myPropertiesDeleteStart: (propertyId = '') =>
    trackEvent('my_properties_delete_start', {
      property_id: String(propertyId || ''),
    }),

  /** User confirmed property deletion */
  myPropertiesDeleteConfirm: (propertyId = '') =>
    trackEvent('my_properties_delete_confirm', {
      property_id: String(propertyId || ''),
    }),

  /** User clicked Edit from My Properties page */
  myPropertiesEdit: (propertyId = '') =>
    trackEvent('my_properties_edit', {
      property_id: String(propertyId || ''),
    }),

  /** User clicked a notification */
  notificationClick: (notificationId = '', category = '') =>
    trackEvent('notification_click', {
      notification_id: String(notificationId || ''),
      category,
    }),

  /** User marked all notifications as read */
  notificationsMarkAllRead: (count = 0) =>
    trackEvent('notifications_mark_all_read', {
      notification_count: Number(count) || 0,
    }),

  /** User deleted a single notification */
  notificationDelete: (notificationId = '') =>
    trackEvent('notification_delete', {
      notification_id: String(notificationId || ''),
    }),

  /** User signed up via OAuth provider */
  oauthSignUpClick: (provider = '') =>
    trackEvent('sign_up', { method: provider }),

  /** User clicked "Forgot password?" on the login page */
  forgotPasswordClick: () =>
    trackEvent('login_forgot_password_click', {}),

  myPropertyMarkMLSReady: (propertyId = '') =>
    trackEvent('my_property_mark_mls_ready', {
      property_id: String(propertyId || ''),
    }),

  /** User clicked "Similar Properties" button from My Property page */
  similarPropertiesClick: (propertyId = '') =>
    trackEvent('similar_properties_click', {
      property_id: String(propertyId || ''),
      content_type: 'property',
    }),

  /** User clicked "Selling Journey" button from My Property page */
  sellingJourneyClick: (propertyId = '') =>
    trackEvent('selling_journey_click', {
      property_id: String(propertyId || ''),
      content_type: 'property',
    }),

  /** User submitted a search query from the Home page chat bar */
  homeSearch: (query = '') =>
    trackEvent('home_search', {
      search_term: query.trim().slice(0, 100),
      content_type: 'ai_chat',
    }),

  /** User clicked a feature chip (example query) on the Home page */
  homeFeatureChipClick: (featureTitle = '') =>
    trackEvent('home_feature_chip_click', {
      feature: featureTitle,
    }),

  /** Voice input was activated from a specific UI surface */
  voiceInputStart: ({ surface = 'unknown', trigger = 'manual' } = {}) =>
    trackEvent('voice_input_start', {
      surface,
      trigger,
    }),

  /** Voice input stopped from a specific UI surface */
  voiceInputStop: ({ surface = 'unknown', reason = 'user' } = {}) =>
    trackEvent('voice_input_stop', {
      surface,
      reason,
    }),

  /** Voice input could not start because of permission/browser issues */
  voiceInputError: ({ surface = 'unknown', error = 'unknown' } = {}) =>
    trackEvent('voice_input_error', {
      surface,
      error,
    }),

  /** Chat talk mode was toggled on/off */
  chatTalkModeToggle: ({ enabled = false } = {}) =>
    trackEvent('chat_talk_mode_toggle', {
      enabled: !!enabled,
      surface: 'chat',
    }),

  /** User clicked an OAuth provider button on Login / Signup */
  oauthLoginClick: (provider = '') =>
    trackEvent('login', {
      method: provider,
    }),

  /** User submitted the Schedule Consultation form */
  scheduleConsultationSubmit: ({ propertyId = '', consultationTopic = '' } = {}) =>
    trackEvent('schedule_consultation_submit', {
      property_id: String(propertyId || ''),
      consultation_topic: consultationTopic,
    }),

  /** Backward-compatible alias for older callers */
  scheduleShowingSubmit: ({ propertyId = '', showingType = '' } = {}) =>
    trackEvent('schedule_consultation_submit', {
      property_id: String(propertyId || ''),
      consultation_topic: showingType,
    }),
};

export default analytics;
